import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, User, Settings, Users, FileText, Megaphone, Calendar, Waves } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useNotifications } from '../../hooks/useNotifications';
import { CommunityGuidelinesModal } from '../UI';
import styles from './MobileHeader.module.css';

const MobileHeader: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfile, setShowProfile] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);

  const {
    notifications,
    unreadCount,
    showNotifications,
    setShowNotifications,
    handleMarkAllRead,
    handleNotificationClick,
    formatTime,
  } = useNotifications();

  const getInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formatRole = (role: string) => {
    if (role === 'superadmin') return 'Super Admin';
    if (role === 'manager') return 'Mentor';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeAll = () => {
    setShowNotifications(false);
    setShowProfile(false);
  };

  return (
    <>
      <div className={styles.mobileHeader}>
        <div className={styles.decorCircle} />

        <div className={styles.logoRow}>
          <div className={styles.logoWrap} onClick={() => navigate('/dashboard')}>
            <img src="/logo.svg" alt="RIPPLE" className={styles.logoImg} />
          </div>

          <div className={styles.headerActions}>
            <button
              className={styles.notifBtn}
              onClick={() => { setShowNotifications(!showNotifications); setShowProfile(false); }}
            >
              <span className={styles.notifIcon} />
              {unreadCount > 0 && <span className={styles.notifDot} />}
            </button>

            <button className={styles.avatarBtn} onClick={() => { setShowProfile(!showProfile); setShowNotifications(false); }}>
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className={styles.avatar} />
              ) : (
                <div className={styles.avatarPlaceholder}>
                  {user && getInitials(user.first_name, user.last_name)}
                </div>
              )}
            </button>
          </div>
        </div>

        <div className={styles.greeting}>
          {getGreeting()}, {user?.first_name}
        </div>

        <div className={styles.statsRow}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>TO REDEEM</span>
            <span className={styles.statValueMint}>{Math.max(0, (user?.points_balance ?? 0) - 500)}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>LEFT TO GIVE</span>
            <span className={styles.statValueWhite}>{user?.points_balance ?? 0}</span>
          </div>
        </div>

        <div className={styles.quickLinks}>
          <button
            className={`${styles.quickLink} ${location.pathname === '/announcements' ? styles.quickLinkActive : ''}`}
            onClick={() => navigate('/announcements')}
          >
            <Megaphone size={14} />
            <span>Announcements</span>
          </button>
          <button
            className={`${styles.quickLink} ${location.pathname === '/events' ? styles.quickLinkActive : ''}`}
            onClick={() => navigate('/events')}
          >
            <Calendar size={14} />
            <span>Events</span>
          </button>
          <button
            className={`${styles.quickLink} ${location.pathname === '/streams' ? styles.quickLinkActive : ''}`}
            onClick={() => navigate('/streams')}
          >
            <Waves size={14} />
            <span>Streams</span>
          </button>
        </div>
      </div>

      {/* Notification panel — fixed overlay */}
      {showNotifications && (
        <>
          <div className={styles.dropdownBackdrop} onClick={closeAll} />
          <div className={styles.dropdown}>
            <div className={styles.dropdownHeader}>
              <span>Notifications</span>
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className={styles.markAllRead}>
                  Mark all read
                </button>
              )}
            </div>
            <div className={styles.notificationList}>
              {notifications.length === 0 ? (
                <div className={styles.emptyNotifications}>No notifications yet</div>
              ) : (
                notifications.slice(0, 5).map((notif) => (
                  <div
                    key={notif.id}
                    className={`${styles.notifItem} ${!notif.is_read ? styles.unread : ''}`}
                    onClick={() => { handleNotificationClick(notif); closeAll(); }}
                  >
                    <span className={styles.notifTitle}>{notif.title}</span>
                    {notif.message && <span className={styles.notifMessage}>{notif.message}</span>}
                    <span className={styles.notifTime}>{formatTime(notif.created_at)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Profile panel — fixed overlay */}
      {showProfile && (
        <>
          <div className={styles.dropdownBackdrop} onClick={closeAll} />
          <div className={styles.dropdown}>
            <div className={styles.profileInfo}>
              <span className={styles.profileName}>
                {user?.first_name} {user?.last_name}
              </span>
              <span className={styles.profileEmail}>{user?.email}</span>
              <span className={styles.profileRole}>{user?.role ? formatRole(user.role) : ''}</span>
            </div>
            <div className={styles.divider} />
            <button className={styles.menuItem} onClick={() => { closeAll(); navigate('/profile'); }}>
              <User size={16} /><span>My Profile</span>
            </button>
            {(user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'executive' || user?.role === 'mentor') && (
              <button className={styles.menuItem} onClick={() => { closeAll(); navigate('/team'); }}>
                <Users size={16} /><span>My Mentees</span>
              </button>
            )}
            {(user?.role === 'superadmin' || user?.role === 'admin') && (
              <button className={styles.menuItem} onClick={() => { closeAll(); navigate('/settings'); }}>
                <Settings size={16} /><span>Settings</span>
              </button>
            )}
            <button className={styles.menuItem} onClick={() => { closeAll(); setShowGuidelines(true); }}>
              <FileText size={16} /><span>Matasma Collective Principles</span>
            </button>
            <div className={styles.divider} />
            <button className={styles.menuItem} onClick={handleLogout}>
              <LogOut size={16} /><span>Sign Out</span>
            </button>
          </div>
        </>
      )}

      <CommunityGuidelinesModal
        isOpen={showGuidelines}
        onClose={() => setShowGuidelines(false)}
        readOnly
      />
    </>
  );
};

export default MobileHeader;
